import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import DatePicker from './customDataPicker';
import { getApiResponse } from '../../APIs/getApiResponse';
import AttensionSpanIcon from './Icons/AttensionSpanIcon.svg';
import engagementImage from './Icons/engagementIcon.svg';
import emotionalStateIcon from './Icons/emotionalStateIcon.svg';
import CompareVectorIcon from './Icons/CompareVector.svg';
import FocusIcon from './Icons/FocusIcon.svg';
import StrengthIcon from './Icons/StrengthIcon.svg';
import ComparitiveAnalysis from './ComparitiveAnalysis';
import { handleError } from '../../APIs/apiUtils';

const StatCard = ({ title, value, img: Image, isLoading }) => {
    return (
        <div className="flex items-center justify-between p-3 bg-white border rounded-xl transition-shadow duration-200 w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.75rem)] xl:w-64 font-[Archivo]">
            <div className="space-y-0">
                <p className="text-xs md:text-sm text-gray-600 mb-0.5">{title}</p>
                <p className="text-xl md:text-2xl font-bold text-black-800 leading-tight">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-6">
                            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : (
                        value
                    )}
                </p>
            </div>
            {Image && (
                <div className="p-2 rounded-full">
                    <img src={Image} alt={`${title} icon`} className="w-8 h-8" />
                </div>
            )}
        </div>
    );
};

const CompareButton = () => {
    return (
        <div className="inline-flex items-center px-4 py-1 space-x-2 text-black-700 bg-white border border-black-300 rounded-lg hover:bg-black-50 transition-colors z-10">
            <img src={CompareVectorIcon} alt="Compare icon" className="w-3 h-3 md:w-4 md:h-4 text-black-700" />
            <span className="font-medium font-[Archivo] text-xs md:text-sm text-black-700">Compare</span>
        </div>
    );
};

// Modified SubjectPerformanceCard to accept isSingleDate prop
const SubjectPerformanceCard = ({ subject, behaviourScore, classAverage, avgAttentionSpan, avgEngagement, emotionalState, onClick, onCompareClick, className, totalSubjects, isSingleDate }) => {
    const [barWidth, setBarWidth] = useState(0);
    const hasData = emotionalState.toLowerCase() !== 'no data';

    useEffect(() => {
        const timer = setTimeout(() => {
            setBarWidth(behaviourScore);
        }, 100);
        return () => clearTimeout(timer);
    }, [behaviourScore]);

    // Use parseFloat for comparison
    const attentionSpanColor = (parseFloat(avgAttentionSpan) > 60) ? 'text-green-600' : 'text-orange-600';

    const attentionSpanTitle = isSingleDate ? "Attention Span" : "Average Attention Span";
    const engagementTitle = isSingleDate ? "Engagement Rate" : "Average Engagement";

    return (
        <div
            className={`p-4 md:p-6 mb-6 bg-white border rounded-xl cursor-pointer hover:shadow-lg transition-shadow duration-200 font-[Archivo] ${className}`}
            onClick={onClick}
        >
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg md:text-xl font-semibold">{subject}</h3>
                {totalSubjects > 1 && (
                    <div onClick={(e) => {
                        e.stopPropagation();
                        onCompareClick();
                    }}>
                        <CompareButton />
                    </div>
                )}
            </div>
            {!hasData ? (
                <div className="flex items-center justify-center h-24 text-gray-500 text-lg font-semibold">
                    No Data Available
                </div>
            ) : (
                <div className="space-y-1">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold">Behaviour Score</span>
                        <span className="font-bold text-sm">{behaviourScore}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="h-2 rounded-full bg-black transition-all duration-1000 ease-out"
                            style={{ width: `${barWidth}%` }}
                        ></div>
                    </div>
                    <div className="text-xs md:text-sm text-gray-500">
                        <span>Class Average:</span> {classAverage}%
                    </div>
                    <div className="flex flex-col text-left gap-1 pt-1 sm:flex-row sm:justify-between sm:text-left text-xs text-black-700">
                        <div className="lg:text-md md:text-md text-sm">
                            <span>{attentionSpanTitle}:</span> <span className={`font-semibold text-sm ${attentionSpanColor}`}>{avgAttentionSpan}</span>
                        </div>
                        <div className="lg:text-md md:text-md text-sm">
                            <span>{engagementTitle}:</span> <span className="font-semibold text-sm text-blue-600">{avgEngagement}%</span>
                        </div>
                        <div className="lg:text-md md:text-md text-sm">
                            <span>Emotional State:</span> <span className="font-semibold text-sm text-orange-600">{emotionalState}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const SubjectPerformanceCardLoading = () => {
    return (
        <div className="p-4 md:p-6 bg-white border rounded-xl animate-pulse font-[Archivo]">
            <div className="flex items-center justify-between mb-2">
                <div className="h-6 md:h-8 bg-gray-400 rounded w-1/4"></div>
                <div className="h-8 md:h-10 bg-gray-400 rounded w-20"></div>
            </div>
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <div className="h-4 md:h-5 bg-gray-300 rounded w-1/3"></div>
                    <div className="h-4 md:h-5 bg-gray-300 rounded w-1/6"></div>
                </div>
                <div className="w-full bg-gray-300 rounded-full h-2">
                    <div className="h-2 rounded-full bg-gray-300 w-3/4"></div>
                </div>
                <div className="h-3 md:h-4 bg-gray-300 rounded w-1/2"></div>
                <div className="flex flex-col text-left gap-1 pt-1 sm:flex-row sm:justify-between sm:text-left text-xs md:text-sm text-black-700">
                    <div className="h-3 md:h-4 bg-gray-300 rounded w-2/5"></div>
                    <div className="h-3 md:h-4 bg-gray-300 rounded w-1/4"></div>
                    <div className="h-3 md:h-4 bg-gray-300 rounded w-1/4"></div>
                </div>
            </div>
        </div>
    );
}

const InsightsCard = ({ title, items, img: Image, textColor, isLoading }) => {
    if (isLoading) {
        return (
            <div className="p-4 rounded-lg border bg-white animate-pulse font-[Archivo]">
                <div className="flex items-center space-x-2 mb-2">
                    <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                </div>
                <ul className="pl-2 space-y-1">
                    <li className="h-3 bg-gray-200 rounded w-4/5"></li>
                    <li className="h-3 bg-gray-200 rounded w-full"></li>
                    <li className="h-3 bg-gray-200 rounded w-3/4"></li>
                </ul>
            </div>
        );
    }
    return (
        <div className="pt-2 pl-5 rounded-lg border bg-white font-[Archivo]">
            <div className="flex items-center space-x-2">
                <img src={Image} alt={`${title} icon`} className="w-6 h-6 xl:w-7 xl:h-7" />
                <h4 className={`text-sm sm:text-base xl:text-lg mt-2 font-semibold ${textColor}`}>{title}</h4>
            </div>
            <ul className="pl-5 space-y-0 text-md list-disc marker:text-black-400">
                {(items || []).map((item, index) => (
                    <li key={index} className="ml-1 mb-1 text-[#373737] font-[400] text-sm xl:text-base">{item}</li>
                ))}
            </ul>
        </div>
    );
};

const LOCAL_STORAGE_START_DATE_KEY = 'global_start_date';
const LOCAL_STORAGE_END_DATE_KEY = 'global_end_date';

// parseUrlDateToApiFormat is not directly used in this snippet's logic flow for dates,
// but kept as it was in the original code.
const parseUrlDateToApiFormat = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateString;
};

const StudentProfileBody = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id, name } = useParams();

    const student_name = name;
    const studentId = id;
    const school_name = localStorage.getItem('school_name');

    const [startDate, setStartDate] = useState(() => {
        const storedStartDate = localStorage.getItem(LOCAL_STORAGE_START_DATE_KEY);
        // Ensure that if it's null, we don't store "null" as a string
        return storedStartDate !== 'null' ? storedStartDate : null;
    });

    const [endDate, setEndDate] = useState(() => {
        const storedEndDate = localStorage.getItem(LOCAL_STORAGE_END_DATE_KEY);
        return storedEndDate !== 'null' ? storedEndDate : null;
    });

    const [studentDataLoading, setStudentDataLoading] = useState(true);
    const [subjectsDataLoading, setSubjectsDataLoading] = useState(true);
    const [insightsDataLoading, setInsightsDataLoading] = useState(true);

    const [studentData, setStudentData] = useState(null);
    const [subjectsData, setSubjectsData] = useState(null);
    const [insightsData, setInsightsData] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState(null);

    // Determine if it's a single date selection
    const isSingleDateSelected = startDate && endDate && (startDate === endDate);

    useEffect(() => {
        if (startDate) {
            localStorage.setItem(LOCAL_STORAGE_START_DATE_KEY, startDate);
        } else {
            localStorage.removeItem(LOCAL_STORAGE_START_DATE_KEY);
        }
    }, [startDate]);

    useEffect(() => {
        if (endDate) {
            localStorage.setItem(LOCAL_STORAGE_END_DATE_KEY, endDate);
        } else {
            localStorage.removeItem(LOCAL_STORAGE_END_DATE_KEY);
        }
    }, [endDate]);

    useEffect(() => {
        const fetchStudentData = async () => {
            setStudentDataLoading(true);
            try {
                let studentApiUrl = `/api/student/engage/${studentId}/?school=${school_name}`;
                if (startDate) studentApiUrl += `&start_date=${startDate}`;
                if (endDate) studentApiUrl += `&end_date=${endDate}`;
                const studentResponse = await getApiResponse(studentApiUrl);
                setStudentData(studentResponse.data);
            } catch (error) {
                setStudentData(null);
                handleError(error);
            } finally {
                setStudentDataLoading(false);
            }
        };

        const fetchSubjectsData = async () => {
            setSubjectsDataLoading(true);
            try {
                let subjectsApiUrl = `/api/students/report/${studentId}/?school=${school_name}`;
                if (startDate) subjectsApiUrl += `&start_date=${startDate}`;
                if (endDate) subjectsApiUrl += `&end_date=${endDate}`;
                const subjectsResponse = await getApiResponse(subjectsApiUrl);
                setSubjectsData(subjectsResponse.data);
            } catch (error) {
                setSubjectsData(null);
                handleError(error);
            } finally {
                setSubjectsDataLoading(false);
            }
        };

        const fetchInsightsData = async () => {
            setInsightsDataLoading(true);
            try {
                let insightsApiUrl = `/api/student-insights/${studentId}/?school=${school_name}`;
                if (startDate) insightsApiUrl += `&start_date=${startDate}`;
                if (endDate) insightsApiUrl += `&end_date=${endDate}`;
                const insightsResponse = await getApiResponse(insightsApiUrl);
                const apiResponseData = insightsResponse.data;
                const formattedInsights = {
                    strengths: apiResponseData.strengths || [],
                    needToFocus: apiResponseData.need_to_focus || [],
                };
                setInsightsData(formattedInsights);
            } catch (error) {
                setInsightsData(null);
                handleError(error);
            } finally {
                setInsightsDataLoading(false);
            }
        };

        fetchStudentData();
        fetchSubjectsData();
        fetchInsightsData();
    }, [studentId, school_name, startDate, endDate]);

    const handleSubjectClick = (subjectName) => () => {
        if (subjectsData) {
            navigate(`/SubjectPerformance/${studentId}/${student_name}/${subjectName}`);
        } else {
            toast.error("Subjects data not available for navigation.");
        }
    };

    const handleCompareClick = (subject) => {
        setSelectedSubject(subject);
        setShowModal(true);
    };

    const formatAttentionSpan = (minutes) => {
        if (minutes === null || minutes === undefined || isNaN(minutes)) return "N/A";
        if (minutes < 1) {
            const seconds = Math.round(minutes * 60);
            return (
                <>
                    <span>{seconds}</span>
                    <span className="text-sm font-semibold ml-1 mr-2 font-[Archivo]">sec/class</span>
                </>
            );
        }
        const integerMinutes = Math.floor(minutes);
        return (
            <>
                <span>{integerMinutes}</span>
                <span className="text-sm font-semibold ml-1 mr-2 font-[Archivo]">min/class</span>
            </>
        );
    };

    const handleNavigateToDashboard = () => {
        localStorage.removeItem(LOCAL_STORAGE_START_DATE_KEY);
        localStorage.removeItem(LOCAL_STORAGE_END_DATE_KEY);
        setStartDate(null);
        setEndDate(null);
        navigate("/");
    };

    return (
        <div className="flex flex-col h-full px-2 md:px-4 lg:px-6 py-2 md:py-4 lg:py-6 font-[Archivo] space-y-6">
            {/* First section: Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col space-y-1">
                    <div className="flex items-center text-md text-black-800 flex-wrap">
                        <span
                            className="pr-2 cursor-pointer hover:text-blue-600"
                            onClick={() => handleNavigateToDashboard()}
                        >
                            Behaviour Dashboard
                        </span>
                        <span className="pr-2">&gt;</span>
                        <span className="text-blue-600">{student_name} Profile</span>
                    </div>
                    <h1 className="text-xl sm:text-2xl text-[#000000] font-[500] leading-tight">
                        {student_name} Performance
                    </h1>
                    <p className="text-xs md:text-sm text-[#000000] font-[400]">
                        Detailed breakdown of {student_name}'s performance across all subjects
                    </p>
                </div>
                <DatePicker
                    onDateSelect={(start, end) => {
                        setStartDate(start);
                        setEndDate(end);
                    }}
                    initialStartDate={startDate}
                    initialEndDate={endDate}
                />
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-5">
                <StatCard
                    title="Average Attention Span"
                    value={
                        studentDataLoading
                            ? null
                            : formatAttentionSpan(studentData?.average_attention_minutes)
                    }
                    img={AttensionSpanIcon}
                    isLoading={studentDataLoading}
                />
                <StatCard
                    title="Average Engagement"
                    value={
                        studentDataLoading
                            ? null
                            : studentData?.average_engagement_percent !== null &&
                                studentData?.average_engagement_percent !== undefined
                                ? `${studentData.average_engagement_percent}%`
                                : "N/A"
                    }
                    img={engagementImage}
                    isLoading={studentDataLoading}
                />
               <StatCard
                    title="Emotional State"
                    value={studentDataLoading ? null : (studentData?.highest_emotion ? (studentData.highest_emotion === 'sad' ? 'Not Interested' : (studentData.highest_emotion.charAt(0).toUpperCase() + studentData.highest_emotion.slice(1))) : "N/A")}
                    img={emotionalStateIcon}
                    isLoading={studentDataLoading}
                    />
            </div>
            <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex flex-col flex-1 max-h-[700px] overflow-y-auto scrollbar-hide">
                    {subjectsDataLoading ? (
                        <>
                            <SubjectPerformanceCardLoading />
                            <SubjectPerformanceCardLoading />
                            <SubjectPerformanceCardLoading />
                        </>
                    ) : subjectsData?.subjects?.length > 0 ? (
                        subjectsData.subjects.map((subject, index) => (
                            <SubjectPerformanceCard
                                key={index}
                                subject={subject.subject}
                                behaviourScore={
                                    subject.student_behaviour_score?.toFixed(1) || 0
                                }
                                classAverage={subject.total_class_behaviour?.toFixed(1) || 0}
                                avgAttentionSpan={formatAttentionSpan(
                                    subject.attention_minutes_per_period
                                )}
                                avgEngagement={subject.average_engagement || 0}
                                emotionalState={
                                    subject.highest_emotion === 'sad'
                                      ? 'Not Interested'
                                      : subject.highest_emotion
                                        ? subject.highest_emotion.charAt(0).toUpperCase() + subject.highest_emotion.slice(1)
                                        : 'No Data'
                                  }
                                  
                                onClick={handleSubjectClick(subject.subject)}
                                onCompareClick={() => handleCompareClick(subject.subject)}
                                totalSubjects={subjectsData.subjects.length}
                                isSingleDate={isSingleDateSelected}
                            />
                        ))
                    ) : (
                        subjectsData?.subjects?.length > 0 ? (
                            subjectsData.subjects.map((subject, index) => (
                                <SubjectPerformanceCard
                                    key={index}
                                    subject={subject.subject}
                                    behaviourScore={subject.student_behaviour_score?.toFixed(1) || 0}
                                    classAverage={subject.total_class_behaviour?.toFixed(1) || 0}
                                    avgAttentionSpan={formatAttentionSpan(subject.attention_minutes_per_period)}
                                    avgEngagement={subject.average_engagement || 0}
                                    emotionalState={subject.highest_emotion === 'sad' ? 'Not Interested' : (subject.highest_emotion ? (subject.highest_emotion.charAt(0).toUpperCase() + subject.highest_emotion.slice(1)) : 'No Data')}                                    onClick={handleSubjectClick(subject.subject)}
                                    onCompareClick={() => handleCompareClick(subject.subject)}
                                    className={index === subjectsData.subjects.length - 1 ? '' : ''}
                                    totalSubjects={subjectsData.subjects.length}
                                />
                            ))
                        ) : (
                            <div className="p-4 bg-white border rounded-xl text-center text-gray-500">No subjects data found for this student.</div>
                        )
                    )}
                </div>
                <div className="flex flex-col w-full lg:w-1/3 space-y-5">
                    {insightsDataLoading ? (
                        <>
                            <InsightsCard title="Strengths" items={[]} isLoading={true} />
                            <InsightsCard title="Need to Focus" items={[]} isLoading={true} />
                        </>
                    ) : (
                        insightsData && (
                            <>
                                <InsightsCard
                                    title="Strengths"
                                    items={insightsData.strengths}
                                    img={StrengthIcon}
                                    textColor="text-green-600"
                                />
                                <InsightsCard
                                    title="Need to Focus"
                                    items={insightsData.needToFocus}
                                    img={FocusIcon}
                                    textColor="text-red-600"
                                />
                            </>
                        )
                    )}
                </div>
            </div>

            {showModal && selectedSubject && (
                <ComparitiveAnalysis
                    show={showModal}
                    onClose={() => setShowModal(false)}
                    subject={selectedSubject}
                    allSubjects={subjectsData?.subjects || []}
                    studentId={studentId}
                    DatePicker={{ startDate, endDate }}
                    school_name={school_name}
                />
            )}
        </div>
    );

};

export default StudentProfileBody;